import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  Paper,
  Avatar,
  Divider
} from '@mui/material';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

interface Message {
  id: string;
  text: string;
  senderId: string;
  timestamp: any;
  senderName: string;
  senderPhotoURL?: string;
}

interface ChatSectionProps {
  userId: string;
}

const ChatSection: React.FC<ChatSectionProps> = ({ userId }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    if (!userId) return;

    // Créer ou récupérer la conversation
    const setupConversation = async () => {
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        // Créer une nouvelle conversation
        const newConversation = await addDoc(conversationsRef, {
          participants: [userId],
          createdAt: serverTimestamp()
        });
        setConversationId(newConversation.id);
      } else {
        setConversationId(querySnapshot.docs[0].id);
      }
    };

    setupConversation();
  }, [userId, db]);

  useEffect(() => {
    if (!conversationId) return;

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Message[];
      setMessages(newMessages);
    });

    return () => unsubscribe();
  }, [conversationId, db]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversationId || !auth.currentUser) return;

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    await addDoc(messagesRef, {
      text: newMessage,
      senderId: auth.currentUser.uid,
      senderName: auth.currentUser.displayName || 'Anonyme',
      senderPhotoURL: auth.currentUser.photoURL,
      timestamp: serverTimestamp()
    });

    setNewMessage('');
  };

  return (
    <Paper elevation={3} sx={{ p: 2, height: '400px', display: 'flex', flexDirection: 'column', backgroundImage: 'url("/images/welcome-dress.jpg")' }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
        <List>
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: message.senderId === auth.currentUser?.uid ? 'row-reverse' : 'row',
                  alignItems: 'flex-start'
                }}
              >
                <Avatar
                  src={message.senderPhotoURL}
                  sx={{ mx: 1 }}
                />
                <Box
                  sx={{
                    maxWidth: '70%',
                    backgroundColor: message.senderId === auth.currentUser?.uid ? '#C9B6AB' : '#f5f5f5',
                    borderRadius: 2,
                    p: 1,
                    color: message.senderId === auth.currentUser?.uid ? 'white' : 'text.primary'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {message.senderName}
                  </Typography>
                  <Typography variant="body1">{message.text}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'right' }}>
                    {message.timestamp?.toDate().toLocaleTimeString()}
                  </Typography>
                </Box>
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Écrivez votre message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
          sx={{ backgroundColor: '#C9B6AB', '&:hover': { backgroundColor: '#C1A08E' } }}
        >
          Envoyer
        </Button>
      </Box>
    </Paper>
  );
};

export default ChatSection; 